// 8-step interactive onboarding tour. Linear flow, skippable at any point.
// Each step is a self-contained mini-component with its own state.

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RhythmStrip from './RhythmStrip';
import type { Rhythm } from '../../lib/waveforms';

const TOTAL_STEPS = 8;

export default function Tour() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  function next() { if (step < TOTAL_STEPS) setStep(step + 1); else nav('/dashboard'); }
  function prev() { if (step > 1) setStep(step - 1); }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center mb-6">
        <p className="text-xs text-mono-white/40 uppercase tracking-widest">Familiarisation tour</p>
        <div className="flex items-center gap-3">
          <span className="text-mono-white/60 text-sm font-mono">Step {step} / {TOTAL_STEPS}</span>
          <Link to="/dashboard" className="text-mono-white/50 hover:text-mono-white text-sm">Skip →</Link>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-ink-800 rounded-full h-1.5 mb-8 overflow-hidden">
        <div className="bg-brand-600 h-full transition-all duration-500" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
      </div>

      <div className="panel p-6 mb-6 min-h-[480px]">
        {step === 1 && <StepWelcome />}
        {step === 2 && <StepMonitor />}
        {step === 3 && <StepRhythms />}
        {step === 4 && <StepCPR />}
        {step === 5 && <StepDefib />}
        {step === 6 && <StepDrugs />}
        {step === 7 && <StepReversibleCauses />}
        {step === 8 && <StepReady />}
      </div>

      <div className="flex justify-between">
        <button onClick={prev} disabled={step === 1} className="btn-ghost disabled:opacity-30">← Previous</button>
        <button onClick={next} className="btn-primary">{step === TOTAL_STEPS ? 'Go to dashboard →' : 'Next →'}</button>
      </div>
    </div>
  );
}

// ============ STEP 1 ============
function StepWelcome() {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">Welcome to ALS Sim</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        This is a teaching tool, not a game. The patient on screen responds physiologically to what you do —
        every shock, every drug, every pause in compressions changes their state.
      </p>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        Over the next ~10 minutes you'll meet each tool you'll use in a real scenario: the monitor, rhythms,
        compressions, the defibrillator, the drug cart, and the 4Hs/4Ts. None of these steps will hurt the patient —
        they're walkthroughs.
      </p>
      <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs">
        <Pill emoji="🫀" label="Real-time physiology" />
        <Pill emoji="📋" label="UK RCUK aligned" />
        <Pill emoji="🔊" label="Audio + voice" />
        <Pill emoji="📈" label="Full debrief" />
        <Pill emoji="↩️" label="Replay any moment" />
        <Pill emoji="🎯" label="Targeted feedback" />
      </div>
      <p className="text-mono-white/40 text-xs mt-6 italic">
        Practice tool · Not for clinical decision-making. Scenarios reviewed by a named ALS instructor before publication.
      </p>
    </div>
  );
}

function Pill({ emoji, label }: { emoji: string; label: string }) {
  return (
    <div className="panel p-3 bg-ink-800/50">
      <div className="text-2xl mb-1">{emoji}</div>
      <div className="text-mono-white/70">{label}</div>
    </div>
  );
}

// ============ STEP 2 ============
function StepMonitor() {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">The patient monitor</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        The right-hand panel during a scenario is your <strong>bedside monitor</strong>. It shows three
        live waveforms and the numeric vitals.
      </p>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="panel p-3 bg-ink-800/50">
          <div className="text-mono-green text-xs font-mono mb-2">ECG II — sinus rhythm at 80 bpm</div>
          <RhythmStrip rhythm="sinus" hr={80} />
        </div>
        <div className="panel p-3 bg-ink-800/50">
          <ul className="text-sm space-y-1 text-mono-white/80">
            <li><span className="font-mono text-mono-green">HR</span> — heart rate, derived from rhythm + drugs</li>
            <li><span className="font-mono text-mono-white">BP</span> — non-invasive blood pressure</li>
            <li><span className="font-mono text-mono-yellow">SpO₂</span> — oxygen saturation, pulse-ox</li>
            <li><span className="font-mono text-mono-cyan">EtCO₂</span> — end-tidal CO₂ (key in CPR + post-ROSC)</li>
            <li><span className="font-mono">RR / Temp</span> — respiratory rate, core temperature</li>
            <li><span className="font-mono">Rhythm</span> — current rhythm category</li>
            <li><span className="font-mono">T+</span> — time since scenario start</li>
          </ul>
        </div>
      </div>
      <p className="text-mono-white/60 text-sm mt-4">
        An <span className="text-alarm-red font-semibold">ALARM</span> banner appears for shockable rhythms,
        asystole, PEA, and SpO₂ &lt; 90% in a perfusing patient. You'll hear distinct tones for each.
      </p>
    </div>
  );
}

// ============ STEP 3 ============
type RhythmCategory = 'perfusing' | 'peri_arrest' | 'shockable' | 'non_shockable';
const RHYTHM_QUIZ: Array<{ rhythm: Rhythm; hr: number; label: string; sub: string; category: RhythmCategory }> = [
  // Top row: perfusing baseline + the two shockable arrest rhythms
  { rhythm: 'sinus',        hr: 80,  label: 'Sinus rhythm',             sub: '80 bpm · P-QRS-T regular',           category: 'perfusing' },
  { rhythm: 'vf',           hr: 0,   label: 'Ventricular fibrillation', sub: 'No QRS · chaotic · no pulse',        category: 'shockable' },
  { rhythm: 'vt_pulseless', hr: 180, label: 'Pulseless VT',             sub: '~180 bpm · wide complexes · no pulse', category: 'shockable' },
  // Middle row: non-shockable arrest + peri-arrest brady
  { rhythm: 'asystole',     hr: 0,   label: 'Asystole',                 sub: 'Flat-line · no pulse',               category: 'non_shockable' },
  { rhythm: 'brady',        hr: 38,  label: 'Sinus bradycardia',        sub: '38 bpm · sinus complexes, slow rate', category: 'peri_arrest' },
  { rhythm: 'svt',          hr: 170, label: 'SVT',                      sub: '~170 bpm · narrow, regular, no P waves', category: 'peri_arrest' },
  // Bottom row: AF (peri-arrest if unstable)
  { rhythm: 'af',           hr: 130, label: 'Atrial fibrillation',      sub: '~130 bpm · irregularly irregular · no P waves', category: 'peri_arrest' },
];

const CAT_COLOR: Record<RhythmCategory, string> = {
  perfusing:     '#1aff9c',
  peri_arrest:   '#5be1ff',
  shockable:     '#ff4d4d',
  non_shockable: '#ffb84d',
};
const CAT_LABEL: Record<RhythmCategory, { text: string; classes: string }> = {
  perfusing:     { text: '✓ Perfusing — patient has a pulse, treat the cause', classes: 'text-mono-green' },
  peri_arrest:   { text: '⚠️ Peri-arrest — pulse present but unstable. Treat per peri-arrest algorithm before they crash', classes: 'text-mono-cyan' },
  shockable:     { text: '⚡ Shockable arrest — shock + CPR + adrenaline after 3rd shock', classes: 'text-alarm-red' },
  non_shockable: { text: '✋ Non-shockable arrest — CPR + adrenaline ASAP, NO shock', classes: 'text-alarm-amber' },
};

function StepRhythms() {
  const [reveal, setReveal] = useState(false);
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">Recognising rhythms</h2>
      <p className="text-mono-white/80 mb-2 leading-relaxed">
        ALS hinges on rhythm recognition. Four categories drive everything:
      </p>
      <ul className="text-sm text-mono-white/70 ml-4 list-disc space-y-1 mb-4">
        <li><span className="text-mono-green font-semibold">Perfusing</span> — patient has a pulse and is stable.</li>
        <li><span className="text-mono-cyan font-semibold">Peri-arrest</span> — pulse present but unstable (brady, fast AF / SVT, unstable VT-with-pulse). Treat now or they arrest.</li>
        <li><span className="text-alarm-red font-semibold">Shockable arrest</span> — VF / pulseless VT. Defibrillate.</li>
        <li><span className="text-alarm-amber font-semibold">Non-shockable arrest</span> — asystole / PEA. CPR + adrenaline. NO shock.</li>
      </ul>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {RHYTHM_QUIZ.map((r) => (
          <div key={r.rhythm} className="panel p-3 bg-ink-800/50">
            <RhythmStrip rhythm={r.rhythm} hr={r.hr} color={CAT_COLOR[r.category]} />
            <div className="mt-2 flex justify-between items-start gap-2">
              <div>
                <div className="font-mono text-sm">{r.label}</div>
                <div className="text-[10px] text-mono-white/50 mt-0.5">{r.sub}</div>
              </div>
              {reveal && (
                <span className={`${
                  r.category === 'shockable' ? 'text-alarm-red'
                  : r.category === 'non_shockable' ? 'text-alarm-amber'
                  : r.category === 'peri_arrest' ? 'text-mono-cyan'
                  : 'text-mono-green'} text-[10px] font-semibold uppercase tracking-widest shrink-0`}>
                  {r.category === 'shockable' ? '⚡ shock'
                   : r.category === 'non_shockable' ? 'no shock'
                   : r.category === 'peri_arrest' ? '⚠ peri-arrest'
                   : 'perfusing'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-6">
        {!reveal ? (
          <button onClick={() => setReveal(true)} className="btn-primary">Reveal categories</button>
        ) : (
          <div className="space-y-1 text-xs mt-2 text-left">
            {(['perfusing', 'peri_arrest', 'shockable', 'non_shockable'] as RhythmCategory[]).map((c) => (
              <p key={c} className={CAT_LABEL[c].classes}>{CAT_LABEL[c].text}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ STEP 4 ============
function StepCPR() {
  const [count, setCount] = useState(0);
  const [pulses, setPulses] = useState<number[]>([]);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        pulse();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulses]);

  function pulse() {
    const t = Date.now();
    if (startRef.current === null) startRef.current = t;
    setCount((c) => c + 1);
    setPulses((p) => {
      const next = [...p, t];
      return next.slice(-30);
    });
  }

  function reset() {
    setCount(0);
    setPulses([]);
    startRef.current = null;
  }

  // Compute rate from the last 10 pulses (or all if fewer)
  let rate = 0;
  if (pulses.length >= 2) {
    const window = pulses.slice(-10);
    const span = window[window.length - 1]! - window[0]!;
    if (span > 0) rate = ((window.length - 1) / span) * 60_000;
  }
  const inTarget = rate >= 100 && rate <= 120;
  const rateColor = inTarget ? 'text-mono-green' : rate > 0 ? 'text-alarm-amber' : 'text-mono-white/40';

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">Compressions</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        In a real scenario, each <kbd className="bg-ink-800 border border-ink-800 px-1.5 py-0.5 rounded text-xs font-mono">Space</kbd> press
        or <kbd className="bg-ink-800 border border-ink-800 px-1.5 py-0.5 rounded text-xs font-mono">CPR pulse</kbd> click counts as one compression.
        The system tracks your rate. Target: <span className="text-mono-green font-semibold">100–120 cpm</span>.
      </p>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        Try it now — tap <kbd className="bg-ink-800 border border-ink-800 px-1.5 py-0.5 rounded text-xs font-mono">Space</kbd> rhythmically.
      </p>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="panel p-6 bg-ink-800/50 text-center">
          <div className="text-mono-white/40 text-xs uppercase tracking-widest mb-1">Compressions</div>
          <div className="font-mono text-5xl tabular-nums">{count}</div>
          <button onClick={pulse} onMouseDown={(e) => e.preventDefault()} className="btn-primary mt-4 w-full">Pulse (or press Space)</button>
        </div>
        <div className="panel p-6 bg-ink-800/50 text-center">
          <div className="text-mono-white/40 text-xs uppercase tracking-widest mb-1">Rate</div>
          <div className={`font-mono text-5xl tabular-nums ${rateColor}`}>{rate > 0 ? rate.toFixed(0) : '—'}<span className="text-sm text-mono-white/40 ml-1">cpm</span></div>
          <div className="mt-3 h-3 bg-ink-900 rounded-full overflow-hidden">
            <div className={`h-full transition-all ${inTarget ? 'bg-mono-green' : 'bg-alarm-amber'}`} style={{ width: `${Math.min(100, rate / 1.5)}%` }} />
          </div>
          <p className="text-xs mt-2 text-mono-white/50">Target band 100–120 cpm</p>
          <button onClick={reset} className="text-mono-white/50 text-xs hover:text-mono-white mt-3 underline">Reset</button>
        </div>
      </div>
      <p className="text-mono-white/60 text-sm mt-4">
        <strong>Quality matters as much as rate.</strong> Pauses &gt;10 s reset coronary perfusion pressure and drop shock success.
        Real chest depth (5–6 cm) and complete recoil aren't measurable in software-only mode — webcam-based detection is on the v2 roadmap.
      </p>
    </div>
  );
}

// ============ STEP 5 ============
function StepDefib() {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">The defibrillator</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        Press <kbd className="bg-ink-800 border border-ink-800 px-1.5 py-0.5 rounded text-xs font-mono">D</kbd> for quick shock,
        or open the full <strong>Defib panel</strong> from the toolkit for energy selection, sync cardioversion, and pacing.
      </p>
      <div className="space-y-3 mt-6">
        <Flow num={1} title="Charge" body="Select energy (150–200 J first shock, max thereafter). The defib whines as it charges (~1.4 s) — distinctive sawtooth sweep." color="bg-alarm-amber/15 text-alarm-amber border-alarm-amber/40" />
        <Flow num={2} title="Stand clear" body='Server-prompted safety check. Verbally call "stand clear" — in real life, look at the patient, pads, and team before delivering.' color="bg-brand-600/15 text-brand-600 border-brand-600/40" />
        <Flow num={3} title="Discharge" body="The patient is briefly shocked. Resume CPR immediately — don't wait to see the rhythm." color="bg-alarm-red/15 text-alarm-red border-alarm-red/40" />
      </div>
      <p className="text-mono-white/60 text-sm mt-6 leading-relaxed">
        ⚠️ Common harms the engine catches: <strong>shocking asystole</strong> (no benefit), <strong>shocking PEA</strong> (no benefit), and
        <strong> unsynchronised shock on a perfusing patient</strong> (induces VF via R-on-T). Each triggers a Socratic teach prompt in
        familiarisation mode.
      </p>
    </div>
  );
}

function Flow({ num, title, body, color }: { num: number; title: string; body: string; color: string }) {
  return (
    <div className={`p-3 rounded border-l-4 ${color}`}>
      <div className="flex items-start gap-3">
        <span className="font-mono text-2xl">{num}</span>
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-mono-white/85 mt-1">{body}</p>
        </div>
      </div>
    </div>
  );
}

// ============ STEP 6 ============
function StepDrugs() {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">The drug cart</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        Open the cart from the toolkit. Drugs are grouped by indication: <em>Cardiac arrest</em>, <em>Bradycardia</em>, <em>Reversal</em>, <em>Special circumstances</em>, <em>Vasoactive</em>, <em>Fluids</em>.
      </p>
      <ol className="space-y-2 text-sm text-mono-white/85 list-decimal list-inside ml-2">
        <li><strong>Find</strong> the drug (search bar or scroll).</li>
        <li>Tap to open the <strong>dose calculator</strong>. Default dose is pre-filled.</li>
        <li>Adjust with <kbd className="bg-ink-800 border border-ink-800 px-1.5 rounded text-xs">−</kbd> / <kbd className="bg-ink-800 border border-ink-800 px-1.5 rounded text-xs">+</kbd> buttons. Anything ≥ 10× the default shows a red "confirm intent" warning.</li>
        <li><strong>Draw</strong>, then <strong>Push</strong> — two-tap confirm prevents accidental wrong-drug clicks.</li>
      </ol>
      <div className="panel p-4 mt-6 bg-alarm-amber/10 border-l-4 border-alarm-amber">
        <p className="text-sm text-alarm-amber font-semibold">Wrong-drug / wrong-dose / wrong-timing harms the engine catches:</p>
        <ul className="text-sm text-mono-white/85 mt-2 ml-4 list-disc space-y-1">
          <li><strong>Adrenaline before 3rd shock in shockable arrest</strong> — protocol violation flagged</li>
          <li><strong>Adrenaline in perfusing tachycardia</strong> — degenerates to pulseless VT in ~70 s</li>
          <li><strong>Calcium in suspected digoxin toxicity</strong> — stone heart (asystole in 4 s)</li>
          <li><strong>10× overdose</strong> — HR + 70, SBP + 80, P(ROSC) × 0.3</li>
          <li><strong>Bicarbonate without indication</strong> — paradoxical acidosis, score penalty</li>
        </ul>
      </div>
      <p className="text-mono-white/60 text-sm mt-4">
        The harm engine isn't there to trick you — it's there to teach. Every harm comes with a quick-check teaching prompt in familiarisation mode.
      </p>
    </div>
  );
}

// ============ STEP 7 ============
const FOUR_HS_4TS = [
  { key: 'hypoxia', label: 'Hypoxia' },
  { key: 'hypovolaemia', label: 'Hypovolaemia' },
  { key: 'hyperkalaemia', label: 'Hyper/hypoK+ & metabolic' },
  { key: 'hypothermia', label: 'Hypothermia' },
  { key: 'tension_pneumothorax', label: 'Tension PTX' },
  { key: 'tamponade', label: 'Tamponade' },
  { key: 'toxins', label: 'Toxins' },
  { key: 'thrombosis', label: 'Thrombosis (PE/MI)' },
];

function StepReversibleCauses() {
  const [considered, setConsidered] = useState<Set<string>>(new Set());
  function toggle(key: string) {
    setConsidered((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">Reversible causes (4Hs / 4Ts)</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        Every cardiac arrest scenario in the sim has a <strong>hidden cause</strong>. Until you address it, the patient won't ROSC
        no matter how perfect your CPR. Verbalise each of the 8 and tick them off as you consider them.
      </p>
      <p className="text-mono-white/80 mb-3 leading-relaxed">Try it — tap each chip:</p>
      <div className="flex flex-wrap gap-2">
        {FOUR_HS_4TS.map((c) => (
          <button key={c.key} onClick={() => toggle(c.key)} className={considered.has(c.key) ? 'chip-on' : 'chip-off'}>
            {considered.has(c.key) ? '✓ ' : ''}{c.label}
          </button>
        ))}
      </div>
      <p className="text-mono-white/60 text-sm mt-4">
        {considered.size === 0 && 'Click some chips to mark causes as considered.'}
        {considered.size > 0 && considered.size < 8 && `${considered.size} / 8 considered — keep going.`}
        {considered.size === 8 && '✓ All 8 considered. In a real run, this earns a strength marker in the debrief.'}
      </p>
      <div className="grid md:grid-cols-2 gap-3 mt-6 text-xs">
        <div className="panel p-3">
          <p className="text-mono-cyan font-semibold mb-1">4 Hs</p>
          <p className="text-mono-white/70">Hypoxia · Hypovolaemia · Hyper-/Hypo-K+ &amp; metabolic · Hypothermia</p>
        </div>
        <div className="panel p-3">
          <p className="text-mono-cyan font-semibold mb-1">4 Ts</p>
          <p className="text-mono-white/70">Tension pneumothorax · Tamponade · Toxins · Thrombosis (PE / MI)</p>
        </div>
      </div>
    </div>
  );
}

// ============ STEP 8 ============
function StepReady() {
  return (
    <div>
      <h2 className="text-2xl font-semibold mb-3">You're ready</h2>
      <p className="text-mono-white/80 mb-4 leading-relaxed">
        That's the toolkit. Two final things:
      </p>
      <ul className="space-y-3 text-mono-white/85">
        <li className="flex gap-3">
          <span className="text-2xl">📋</span>
          <div>
            <strong>Algorithm reference</strong> — press <kbd className="bg-ink-800 border border-ink-800 px-1.5 py-0.5 rounded text-xs font-mono">Shift+A</kbd> any time during a scenario to see the full RCUK algorithm overlay. No clock penalty.
          </div>
        </li>
        <li className="flex gap-3">
          <span className="text-2xl">⏯</span>
          <div>
            <strong>Replay your run</strong> — every scenario you complete gets a debrief with vitals timeline, action markers, feedback breakdown, and a scrubbable playback of the entire run. Replay any moment to see what was happening at any instant.
          </div>
        </li>
        <li className="flex gap-3">
          <span className="text-2xl">📊</span>
          <div>
            <strong>Feedback in 12 markers</strong> — recognition latency, time-to-CPR, time-to-first-shock, CCF, drug timing, comms, 4Hs/4Ts coverage, decisions under pressure. Each scored as strength / minor / major / critical.
          </div>
        </li>
        <li className="flex gap-3">
          <span className="text-2xl">🩹</span>
          <div>
            <strong>It's a teaching tool.</strong> Wrong moves harm a synthetic patient and produce a teaching moment, not a real outcome. Take risks — the only cost is a flag in your debrief.
          </div>
        </li>
      </ul>
      <div className="panel p-4 mt-6 bg-brand-600/15 border-l-4 border-brand-600">
        <p className="text-sm">
          Head to the <Link to="/dashboard" className="text-brand-600 hover:underline font-semibold">dashboard</Link> and pick a scenario.
          We recommend starting with <strong>Witnessed VF — monitored bay, A&amp;E resus</strong> if you're new to the algorithm.
        </p>
      </div>
    </div>
  );
}
